import * as React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';
import { withModal } from 'components/modal/ModalHOC';
import UploadPlaceholder from '../upload-placeholder/UploadPlaceholder';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import OutfitCreateModal from 'modules/realm-outfits/components/outfit-create-modal/OutfitCreateModal';
import OutfitMediaToPublish from 'modules/realm-outfits/components/outfit-media-to-publish/OutfitMediaToPublish';
import DeliverableApprovalNote from
  'modules/deliverables/components/deliverable-v2/deliverable-content-approval-tab/deliverable-approval-note/DeliverableApprovalNote';
import PageNotFound from 'components/page/page-not-found/PageNotFound';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';

import {
  editApprovalNote,
  fetchCampaignDeliverables,
  getCreatorlessApprovalNote,
  sendCreatorlessApprovalNote
} from 'modules/product-list/reducers/campaign-outfits/campaign-outfits';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import './ccm-creatorless-outfits.scss';

applyTranslations(require('./ccm-creatorless-outfits.i18n.json'));

import type {
  Campaign,
  DeliverableNote,
  RealmOutfit
} from 'types';

type OwnProps = {
  isModerator?: boolean
}

type StateProps = {
  campaign: Campaign | undefined,
  outfits: Array<RealmOutfit>,
  notes: Array<DeliverableNote>,
  guidelinesUrl: string
};

type DispatchProps = {
  dispatchGetCreatorlessApprovalNote: (campaignId: number) => Promise<void>,
  dispatchSendCreatorlessNote: (
    campaignId: number,
    note: string,
    authoType: Exclude<DeliverableNote['author']['type'], 'external_client' | 'influencer'>
  ) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message?: string | React.ReactNode,
      confirmButtonLabel?: string,
      onConfirm?: () => void,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode
    }
  ) => void,
  dispatchFetchCampaign: (campaignId: number) => Promise<void>,
  dispatchHideConfirmationModal: () => void,
  dispatchEditCreatorlessNote: (campaignId: number, noteId: number, value?: string) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const OUTFIT_GUIDELINES_URL = 'https://collabary.com/guidelines/ccm-outfit-guidelines.pdf';

const UploadOutfitPlaceholder = withModal(UploadPlaceholder, {
  modalContent: OutfitCreateModal,
  closeIconPosition: 'corner',
  closeIconType: 'fancy'
});

const MSG_KEY = 'ccm_creatorless_outfits';

const CcmCreatorlessOutfits: React.FC<Props> = ({
  campaign,
  notes,
  outfits,
  isModerator,
  guidelinesUrl,
  dispatchGetCreatorlessApprovalNote,
  dispatchSendCreatorlessNote,
  dispatchShowConfirmationModal,
  dispatchHideConfirmationModal,
  dispatchFetchCampaign,
  dispatchEditCreatorlessNote
}: Props): JSX.Element => {
  const [approvalNote, setApprovalNote] = React.useState('');
  const [approvalNoteId, setApprovalNoteId] = React.useState<number | null>(null);
  const [isInfoCollapsed, setisInfoCollapsed] = React.useState(true);

  React.useEffect(() => {
    if (campaign?.id) {
      dispatchGetCreatorlessApprovalNote(campaign.id);
      dispatchFetchCampaign(campaign.id);
    }
  }, [campaign?.id]);

  if (!campaign) {
    return <PageNotFound />;
  }

  const iconClasses = classNames('ccm-creatorless-outfits__collapsible-icon', {
    'ccm-creatorless-outfits__collapsible-icon--open': !isInfoCollapsed
  });

  const renderInnerContent = (message: string): React.ReactElement => (
    <div className="ccm-creatorless-outfits__modal-info">
      {message}
    </div>
  );

  const handleApprovalNoteEdit = (id: number, text: string) => {
    if (approvalNote) {
      dispatchShowConfirmationModal({
        title: __t(`${MSG_KEY}.discard_unsaved_note_modal.title`),
        message: __t(`${MSG_KEY}.discard_unsaved_note_modal.info`),
        confirmButtonLabel: __t(`${MSG_KEY}.discard_unsaved_note_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          setApprovalNote(text);
          setApprovalNoteId(id);

          dispatchHideConfirmationModal();
        },
        innerContent: renderInnerContent(__t(`${MSG_KEY}.discard_unsaved_note_modal.confirmation_message`))
      });
    } else {
      setApprovalNote(text);
      setApprovalNoteId(id);
    }
  };

  const handleApprovalNoteDelete = (noteId: number) => {
    dispatchShowConfirmationModal({
      title: __t(`${MSG_KEY}.delete_note_modal.title`),
      message: __t(`${MSG_KEY}.delete_note_modal.info`),
      confirmButtonLabel: __t(`${MSG_KEY}.delete_note_modal.remove_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: async () => {
        if (!noteId) {
          return;
        }

        dispatchHideConfirmationModal();
        dispatchEditCreatorlessNote(campaign.id, noteId);
      },
      innerContent: renderInnerContent(__t(`${MSG_KEY}.delete_note_modal.confirmation_message`))
    });
  };

  const handleCancelApprovalNote = () => {
    setApprovalNote('');
    setApprovalNoteId(null);
  };

  const handleSendApprovalNote = async () => {
    if (approvalNoteId) {
      await dispatchEditCreatorlessNote(campaign.id, approvalNoteId, approvalNote);

      handleCancelApprovalNote();
      return;
    }

    await dispatchSendCreatorlessNote(
      campaign.id,
      approvalNote,
      isModerator
        ? 'moderator'
        : 'brand'
    );

    setApprovalNote('');
  };

  return (
    <div className="ccm-creatorless-outfits">
      <div className="ccm-creatorless-outfits__title">
        {__t(`${MSG_KEY}.title`)}
      </div>
      <div className="ccm-creatorless-outfits__info-header">
        <div className="ccm-creatorless-outfits__sub-title">
          {__t(`${MSG_KEY}.requirements.title`)}
        </div>
        <div
          className={iconClasses}
          onClick={() => setisInfoCollapsed(!isInfoCollapsed)}
        >
          <SVGDynamicIcon
            name="chevron-down-custom-fg"
            height={8}
            width={15}
            customColor="black"
            clickable
          />
        </div>
      </div>
      <CollapsibleContainer
        isExpanded={isInfoCollapsed}
      >
        <div className="ccm-creatorless-outfits__requirements">
          <div>
            <div
              className="ccm-creatorless-outfits__step-title"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(__t(`${MSG_KEY}.requirements.step_1.title`))
              }}
            />
            <div
              className="ccm-creatorless-outfits__info"
              dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(__t(
                `${MSG_KEY}.requirements.step_1.info`,
                { href: guidelinesUrl }
              )) }}
            />
          </div>
          <div>
            <div
              className="ccm-creatorless-outfits__step-title"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(__t(`${MSG_KEY}.requirements.step_2.title`))
              }}
            />
            <div
              className="ccm-creatorless-outfits__info"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(__t(`${MSG_KEY}.requirements.step_2.info`))
              }}
            />
          </div>
          <div>
            <div
              className="ccm-creatorless-outfits__step-title"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(__t(`${MSG_KEY}.requirements.step_3.title`))
              }}
            />
            <div
              className="ccm-creatorless-outfits__info"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(__t(`${MSG_KEY}.requirements.step_3.info`))
              }}
            />
          </div>
        </div>
      </CollapsibleContainer>
      <div className="ccm-creatorless-outfits__outfit">
        <div className="ccm-creatorless-outfits__sub-title">
          {__t(`${MSG_KEY}.outfits.title`)}
        </div>
        <div className="ccm-creatorless-outfits__info">
          {__t(`${MSG_KEY}.outfits.info`)}
        </div>
        <div className="ccm-creatorless-outfits__outfits-container">
          <UploadOutfitPlaceholder
            title={__t(`${MSG_KEY}.outfits.add_outfit_btn`)}
            modalContentProps={{
              genders: campaign?.genders || [],
              mode: 'create',
              campaignName: campaign?.name,
              campaignId: campaign?.id,
              minSKUs: 3,
              maxSKUs: 3
            }}
          />
          {
              outfits.map((outfit, index) => (
                <OutfitMediaToPublish
                  campaign={campaign}
                  key={outfit.id}
                  outfit={outfit}
                  outfitPosition={index}
                  maxSKUs={3}
                  minSKUs={3}
                  isCampaignCreatorless
                />
              ))
            }
        </div>
      </div>
      {
        campaign && (
          <DeliverableApprovalNote
            isCampaignCreatorless
            showNotification={false}
            noteValue={approvalNote}
            noteId={approvalNoteId}
            notes={notes.map(note => ({ ...note, isNew: note.isNew && note.author.type !== 'influencer' }))}
            uniqueId={campaign.id}
            isForBrand
            onNewNoteChange={(value) => setApprovalNote(value)}
            onNoteEdit={handleApprovalNoteEdit}
            onNoteDelete={handleApprovalNoteDelete}
            onSend={handleSendApprovalNote}
            onCancel={handleCancelApprovalNote}
            isZalandoOutfit
          />
        )
      }
    </div>
  );
};

const mapStateToProps = ({
  campaignBrief,
  campaignOutfits,
  runtimeConfig
}): StateProps => ({
  campaign: campaignBrief.brief,
  notes: campaignOutfits.notes,
  outfits: campaignOutfits.outfits,
  guidelinesUrl: runtimeConfig.config.outfitGuidelineLink ?? OUTFIT_GUIDELINES_URL
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaign: (campaignId) => dispatch(fetchCampaignDeliverables(campaignId)),
  dispatchGetCreatorlessApprovalNote: (campaignId: number) => dispatch(getCreatorlessApprovalNote(campaignId)),
  dispatchSendCreatorlessNote: (campaignId: number, value: string, authoType) =>
    dispatch(sendCreatorlessApprovalNote(campaignId, value, authoType)),
  dispatchEditCreatorlessNote: (campaignId: number, noteId: number, value?: string) =>
    dispatch(editApprovalNote(campaignId, noteId, value)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(CcmCreatorlessOutfits);
